import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save, Star, StarOff, CheckCircle2, XCircle, Trash2 } from "lucide-react";

/** Recipes moderation + inline editor for ingredients, steps, hero, seasonal tags. */
export default function CmsRecipes() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/recipes", { params: { status: status === "all" ? undefined : status, limit: 200 } });
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load recipes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  if (selected) return <RecipeEditor rid={selected} onBack={() => { setSelected(null); load(); }} />;

  const moderate = async (rid, newStatus, comment) => {
    try {
      await http.patch(`/admin/recipes/${rid}/moderate`, { status: newStatus, comment });
      toast.success(`Recipe ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Moderation failed");
    }
  };
  const toggleFeature = async (rid, featured) => {
    try {
      await http.patch(`/admin/recipes/${rid}/feature`, { featured });
      toast.success(featured ? "Featured" : "Unfeatured");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  const remove = async (rid) => {
    if (!window.confirm("Delete this recipe? This cannot be undone.")) return;
    try {
      await http.delete(`/admin/recipes/${rid}`);
      toast.success("Recipe deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const STATUSES = ["all", "pending", "published", "rejected", "archived"];

  return (
    <div className="max-w-6xl" data-testid="cms-recipes">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">Recipes</h1>
      <p className="mt-1 text-sm text-slate-500">
        Ingredients, steps, hero photo, seasonal tags — edit inline. {counts.all || 0} total.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`cms-recipes-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`rounded-full border px-3 py-1 text-xs ${status === s ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}
          >
            {s} ({counts[s] ?? 0})
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Recipe</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Featured</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {r.hero_image_url && <img src={r.hero_image_url} alt="" className="h-8 w-8 rounded object-cover" />}
                    <div>
                      <div className="font-medium text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-700">{r.status}</td>
                <td className="px-3 py-2">{r.featured ? "★" : ""}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status !== "published" && (
                    <button onClick={() => moderate(r.id, "published")} data-testid={`cms-recipe-approve-${r.id}`} className="mr-1 inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50">
                      <CheckCircle2 className="h-3 w-3" /> Publish
                    </button>
                  )}
                  {r.status !== "rejected" && r.status !== "archived" && (
                    <button onClick={() => moderate(r.id, "rejected", "Rejected via CMS")} className="mr-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
                      <XCircle className="h-3 w-3" /> Reject
                    </button>
                  )}
                  <button onClick={() => toggleFeature(r.id, !r.featured)} className="mr-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
                    {r.featured ? <StarOff className="h-3 w-3" /> : <Star className="h-3 w-3" />} {r.featured ? "Unfeature" : "Feature"}
                  </button>
                  <button onClick={() => setSelected(r.id)} data-testid={`cms-recipe-edit-${r.id}`} className="mr-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
                    Edit
                  </button>
                  <button onClick={() => remove(r.id)} className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={4} className="p-6 text-center text-slate-500">No recipes match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecipeEditor({ rid, onBack }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Public read endpoint for published recipes — but admin needs draft
        // too. Use /admin/recipes list and pluck the one we need; fall back to
        // /recipes/{slug} for published.
        const r = await http.get("/admin/recipes", { params: { limit: 500 } });
        const found = (r.data?.items || []).find((x) => x.id === rid);
        if (found) {
          // Re-fetch full body from public if published so we get ingredients/steps.
          try {
            const pub = await http.get(`/recipes/${encodeURIComponent(found.slug)}`);
            setDoc({ ...found, ...pub.data });
          } catch {
            setDoc(found);
          }
        } else {
          toast.error("Recipe not found");
          onBack();
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load recipe");
      }
    })();
    // eslint-disable-next-line
  }, [rid]);

  if (!doc) return <div className="p-8 text-slate-500">Loading recipe…</div>;

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const setList = (k, v) => set(k, v.split("\n").map((l) => l.trim()).filter(Boolean));

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/recipes/${doc.id}`, {
        title: doc.title,
        summary: doc.summary,
        hero_image_url: doc.hero_image_url,
        ingredients: doc.ingredients,
        steps: doc.steps,
        seasonal_tags: doc.seasonal_tags,
        prep_minutes: doc.prep_minutes ? Number(doc.prep_minutes) : undefined,
        cook_minutes: doc.cook_minutes ? Number(doc.cook_minutes) : undefined,
        servings: doc.servings ? Number(doc.servings) : undefined,
      });
      toast.success("Recipe saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="cms-recipe-editor">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to recipes
      </button>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <Input value={doc.title || ""} onChange={(e) => set("title", e.target.value)} className="text-2xl font-semibold" data-testid="cms-recipe-title" />
          <div className="mt-1 text-xs text-slate-500">/recipes/{doc.slug}</div>
        </div>
        <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cms-recipe-save">
          <Save className="mr-1 h-4 w-4" /> {busy ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Overview">
          <Field label="Summary"><Textarea rows={3} value={doc.summary || ""} onChange={(e) => set("summary", e.target.value)} /></Field>
          <Field label="Hero image URL"><Input value={doc.hero_image_url || ""} onChange={(e) => set("hero_image_url", e.target.value)} data-testid="cms-recipe-hero" /></Field>
          <Field label="Seasonal tags (comma separated)">
            <Input
              value={(doc.seasonal_tags || []).join(", ")}
              onChange={(e) => set("seasonal_tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
            />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Prep (min)"><Input type="number" value={doc.prep_minutes ?? ""} onChange={(e) => set("prep_minutes", e.target.value)} /></Field>
            <Field label="Cook (min)"><Input type="number" value={doc.cook_minutes ?? ""} onChange={(e) => set("cook_minutes", e.target.value)} /></Field>
            <Field label="Servings"><Input type="number" value={doc.servings ?? ""} onChange={(e) => set("servings", e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Ingredients (one per line)">
          <Textarea
            rows={12}
            data-testid="cms-recipe-ingredients"
            value={(doc.ingredients || []).map((i) => (typeof i === "string" ? i : (i.text || `${i.qty || ""} ${i.unit || ""} ${i.name || ""}`.trim()))).join("\n")}
            onChange={(e) => setList("ingredients", e.target.value)}
          />
        </Section>

        <Section title="Steps (one per line)">
          <Textarea
            rows={12}
            data-testid="cms-recipe-steps"
            value={(doc.steps || []).map((s) => (typeof s === "string" ? s : (s.text || ""))).join("\n")}
            onChange={(e) => setList("steps", e.target.value)}
          />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1 block text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
